import {
  ClipboardList,
  Package,
  Plus,
  Truck,
  X
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import { typography } from '../../styles/typography';
import { getAllSites } from '../SiteManagement/siteApi';
import { Card } from '../ui';
import {
  createRequisition as apiCreateRequisition,
  getDailyPrep as apiGetDailyPrep,
  getStockItem as apiGetStockItem,
  getStockItems as apiGetStockItems,
  getVehiclesBySiteId as apiGetVehiclesBySiteId,
  listRequisitions as apiListRequisitions,
  updateRequisition as apiUpdateRequisition,
} from './centralKitchenApi';

const TABS = {
  REQUISITIONS: 'requisitions',
  DAILY_PREP: 'daily_prep',
  TRANSFERS: 'transfers',
};

const REQ_STATUS = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  IN_PREP: 'in_prep',
  DISPATCHED: 'dispatched',
  IN_TRANSIT: 'in_transit',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
};

const REQ_STATUS_LABELS = {
  [REQ_STATUS.DRAFT]: 'Draft',
  [REQ_STATUS.SUBMITTED]: 'Submitted',
  [REQ_STATUS.IN_PREP]: 'In prep',
  [REQ_STATUS.DISPATCHED]: 'Dispatched',
  [REQ_STATUS.IN_TRANSIT]: 'In transit',
  [REQ_STATUS.DELIVERED]: 'Delivered',
  [REQ_STATUS.CANCELLED]: 'Cancelled',
};

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const formatDateForInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');
const today = () => formatDateForInput(new Date());

// Dummy data (commented out — use API / real data)
// const DUMMY_SITES = [
//   { site_id: 'site-001', site_name: 'Koramangala Outlet' },
//   { site_id: 'site-002', site_name: 'Indiranagar Branch' },
//   { site_id: 'site-003', site_name: 'Whitefield Center' },
// ];

// const DUMMY_ITEMS = [
//   { item_id: 'item-1', item_name: 'Fries (processed)', unit: 'kg' },
//   { item_id: 'item-2', item_name: 'Pizza base', unit: 'pcs' },
//   { item_id: 'item-3', item_name: 'Tomato sauce', unit: 'L' },
//   { item_id: 'item-4', item_name: 'Mozzarella', unit: 'kg' },
//   { item_id: 'item-5', item_name: 'Coffee beans', unit: 'kg' },
// ];

// const DUMMY_REQUISITIONS = [
//   { requisition_id: 'req-001', outlet_id: 'site-001', outlet_name: 'Koramangala Outlet', requested_items_summary: 'Fries 10 kg, Pizza base 20 pcs', item_count: 2, requested_date: new Date().toISOString().slice(0, 10), needed_by_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10), status: REQ_STATUS.SUBMITTED, delivery_vehicle_id: null, delivery_vehicle_name: null, line_items: [{ item_id: 'item-1', item_name: 'Fries (processed)', quantity: 10, unit: 'kg', notes: '' }, { item_id: 'item-2', item_name: 'Pizza base', quantity: 20, unit: 'pcs', notes: '' }] },
//   { requisition_id: 'req-002', outlet_id: 'site-002', outlet_name: 'Indiranagar Branch', requested_items_summary: 'Tomato sauce 5 L, Mozzarella 3 kg', item_count: 2, requested_date: new Date().toISOString().slice(0, 10), needed_by_date: new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10), status: REQ_STATUS.IN_PREP, delivery_vehicle_id: null, delivery_vehicle_name: null, line_items: [{ item_id: 'item-3', item_name: 'Tomato sauce', quantity: 5, unit: 'L', notes: '' }, { item_id: 'item-4', item_name: 'Mozzarella', quantity: 3, unit: 'kg', notes: '' }] },
//   { requisition_id: 'req-003', outlet_id: 'site-001', outlet_name: 'Koramangala Outlet', requested_items_summary: 'Coffee beans 2 kg', item_count: 1, requested_date: new Date(Date.now() - 86400000).toISOString().slice(0, 10), needed_by_date: new Date().toISOString().slice(0, 10), status: REQ_STATUS.DISPATCHED, delivery_vehicle_id: 'v1', delivery_vehicle_name: 'Van 01', dispatched_at: new Date(Date.now() - 3600000).toISOString(), line_items: [{ item_id: 'item-5', item_name: 'Coffee beans', quantity: 2, unit: 'kg', notes: 'Urgent' }] },
//   { requisition_id: 'req-004', outlet_id: 'site-003', outlet_name: 'Whitefield Center', requested_items_summary: 'Fries 15 kg, Tomato sauce 3 L', item_count: 2, requested_date: new Date().toISOString().slice(0, 10), needed_by_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10), status: REQ_STATUS.DRAFT, delivery_vehicle_id: null, delivery_vehicle_name: null, line_items: [{ item_id: 'item-1', item_name: 'Fries (processed)', quantity: 15, unit: 'kg', notes: '' }, { item_id: 'item-3', item_name: 'Tomato sauce', quantity: 3, unit: 'L', notes: '' }] },
// ];

// const DUMMY_DAILY_PREP = [
//   { item_id: 'item-1', item_name: 'Fries (processed)', unit: 'kg', total_quantity: 25, outlets: [{ outlet_name: 'Koramangala Outlet', quantity: 10, notes: '' }, { outlet_name: 'Whitefield Center', quantity: 15, notes: '' }] },
//   { item_id: 'item-2', item_name: 'Pizza base', unit: 'pcs', total_quantity: 20, outlets: [{ outlet_name: 'Koramangala Outlet', quantity: 20, notes: '' }] },
//   { item_id: 'item-3', item_name: 'Tomato sauce', unit: 'L', total_quantity: 8, outlets: [{ outlet_name: 'Indiranagar Branch', quantity: 5, notes: '' }, { outlet_name: 'Whitefield Center', quantity: 3, notes: '' }] },
//   { item_id: 'item-4', item_name: 'Mozzarella', unit: 'kg', total_quantity: 3, outlets: [{ outlet_name: 'Indiranagar Branch', quantity: 3, notes: '' }] },
// ];

// const DUMMY_TRANSFERS = [
//   { transfer_id: 'req-003', requisition_id: 'req-003', outlet_name: 'Koramangala Outlet', items_summary: 'Coffee beans 2 kg', dispatched_at: new Date(Date.now() - 3600000).toISOString(), delivery_vehicle_id: 'v1', delivery_vehicle_name: 'Van 01', status: REQ_STATUS.IN_TRANSIT, expected_by: new Date().toISOString().slice(0, 10) },
// ];

// const DUMMY_VEHICLES = [
//   { vehicle_id: 'v1', vehicle_name: 'Van 01' },
//   { vehicle_id: 'v2', vehicle_name: 'Van 02' },
// ];

/** Normalize API requisition to UI shape (status, line_items.quantity, outlet_name, etc.) */
const normalizeRequisition = (r, sites = [], itemNamesMap = {}) => {
  const outlet = sites.find((s) => (s.site_id || s.id) === (r.outlet_id || r.site_id));
  const outlet_name = outlet?.site_name || outlet?.name || r.outlet_id || r.site_id || '—';
  const line_items = (r.line_items || []).map((li) => {
    const item_id = li.item_id || li.requisition_item_id || li.stock_item_id || '';
    const quantity = li.requested_quantity ?? li.quantity ?? 0;
    const item_name = itemNamesMap[item_id] || li.item_name || li.name || li.product_name || '—';
    return {
      item_id,
      item_name,
      quantity: Number(quantity),
      unit: li.unit || '',
      notes: li.notes || '',
    };
  });
  const requested_items_summary = line_items.map((li) => `${li.item_name} ${li.quantity} ${li.unit}`).join(', ');
  return {
    requisition_id: r.requisition_id,
    outlet_id: r.outlet_id || r.site_id,
    outlet_name,
    requested_items_summary,
    item_count: line_items.length,
    requested_date: r.created_at ? r.created_at.slice(0, 10) : '',
    needed_by_date: r.needed_by_date || '',
    status: r.transfer_status || r.status || REQ_STATUS.DRAFT,
    delivery_vehicle_id: r.delivery_vehicle_id || null,
    delivery_vehicle_name: r.delivery_vehicle_name || null,
    dispatched_at: r.dispatched_at || null,
    updated_at: r.updated_at || null,
    line_items,
  };
};

/** Derive transfers from requisitions (submitted, dispatched, in_transit, delivered) */
const requisitionsToTransfers = (requisitions, sites = []) => {
  const statuses = [REQ_STATUS.SUBMITTED, REQ_STATUS.DISPATCHED, REQ_STATUS.IN_TRANSIT, REQ_STATUS.DELIVERED];
  return requisitions
    .filter((r) => statuses.includes(r.status))
    .map((r) => {
      const outlet = sites.find((s) => (s.site_id || s.id) === r.outlet_id);
      return {
        transfer_id: r.requisition_id,
        requisition_id: r.requisition_id,
        outlet_id: r.outlet_id,
        outlet_name: r.outlet_name || outlet?.site_name || r.outlet_id,
        items_summary: r.requested_items_summary || '',
        dispatched_at: r.dispatched_at || null,
        updated_at: r.updated_at || null,
        delivery_vehicle_id: r.delivery_vehicle_id || null,
        delivery_vehicle_name: r.delivery_vehicle_name || null,
        status: r.status,
        expected_by: r.needed_by_date || '',
      };
    });
};

const CentralKitchen = () => {
  // Log screen view
  useScreenLogging('CentralKitchen', {
    component: 'CentralKitchen',
  });

  const [activeTab, setActiveTab] = useState(TABS.REQUISITIONS);
  const [sites, setSites] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [dailyPrepItems, setDailyPrepItems] = useState([]);
  const [requisitionsLoading, setRequisitionsLoading] = useState(true);
  const [requisitionsError, setRequisitionsError] = useState(null);
  const [dailyPrepLoading, setDailyPrepLoading] = useState(false);
  const [dailyPrepError, setDailyPrepError] = useState(null);
  const [saveInProgress, setSaveInProgress] = useState(false);

  // Requisitions filters
  const [reqSiteFilter, setReqSiteFilter] = useState('');
  const [reqDateFrom, setReqDateFrom] = useState('');
  const [reqDateTo, setReqDateTo] = useState('');
  const [reqStatusFilter, setReqStatusFilter] = useState('');

  // Daily prep filters
  const [prepDate, setPrepDate] = useState(today());
  const [prepOutletFilter, setPrepOutletFilter] = useState('');

  // Modals
  const [requisitionModal, setRequisitionModal] = useState(null); // null | { mode: 'create'|'edit', requisition?: }
  const [requisitionForm, setRequisitionForm] = useState({ outlet_id: '', needed_by_date: '', line_items: [{ item_id: '', quantity: '', unit: '', notes: '' }] });
  const [transferStatusModal, setTransferStatusModal] = useState(null); // { transfer }
  const [transferStatusValue, setTransferStatusValue] = useState('');
  const [vehicleModal, setVehicleModal] = useState(null); // { transfer }
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [vehiclesError, setVehiclesError] = useState(null);
  const [cancelRequisitionConfirm, setCancelRequisitionConfirm] = useState(null); // requisition to cancel (warning dialog)

  const [stockItems, setStockItems] = useState([]);
  const [stockItemsLoading, setStockItemsLoading] = useState(false);
  const [allStockItemsForNames, setAllStockItemsForNames] = useState([]);

  const itemNamesMap = useMemo(() => {
    const m = {};
    const items = [...allStockItemsForNames, ...stockItems];
    items.forEach((i) => {
      const name = i.item_name || i.name || '';
      const id = i.stock_item_id || i.item_id;
      if (id) m[id] = name;
      if (i.stock_item_id && i.item_id && i.stock_item_id !== i.item_id) m[i.item_id] = name;
    });
    return m;
  }, [allStockItemsForNames, stockItems]);

  const fetchRequisitions = useCallback(async () => {
    const listForNormalize = sites.length ? sites : [];
    setRequisitionsLoading(true);
    setRequisitionsError(null);
    try {
      const res = await apiListRequisitions();
      console.log('[CentralKitchen] Transfers / Requisitions list API response:', res);
      const list = res?.requisitions || [];
      
      // Log requisitions fetch
      await loggingService.logInfo('central_kitchen_fetch_requisitions', 'Fetched requisitions list', {
        count: list.length,
        hasFilters: !!(reqSiteFilter || reqStatusFilter || reqDateFrom || reqDateTo),
      });
      const outletIds = [...new Set(list.map((r) => r.outlet_id || r.site_id).filter(Boolean))];
      const nameMap = { ...itemNamesMap };
      if (outletIds.length > 0) {
        const allItemsByOutlet = await Promise.all(
          outletIds.map((outletId) => apiGetStockItems({ site_id: outletId }).then((r) => r?.items ?? []))
        );
        allItemsByOutlet.flat().forEach((i) => {
          const name = i.item_name || i.name || '';
          const id = i.stock_item_id || i.item_id;
          if (id) nameMap[id] = name;
          if (i.stock_item_id && i.item_id && i.stock_item_id !== i.item_id) nameMap[i.item_id] = name;
        });
      }
      const normalized = list.map((r) => normalizeRequisition(r, listForNormalize, nameMap));
      setRequisitions(normalized.length > 0 ? normalized : []);
    } catch (e) {
      console.error('Failed to fetch requisitions:', e);
      setRequisitionsError(e?.message || 'Failed to load requisitions');
      setRequisitions([]);
      
      // Log error
      await loggingService.logError('central_kitchen_fetch_requisitions_error', 'Failed to fetch requisitions', e, {
        errorMessage: e?.message,
      });
    } finally {
      setRequisitionsLoading(false);
    }
  }, [sites, itemNamesMap]);

  const fetchDailyPrep = useCallback(async (prepDateValue) => {
    const listForNormalize = sites.length ? sites : [];
    setDailyPrepLoading(true);
    setDailyPrepError(null);
    try {
      const res = await apiGetDailyPrep(prepDateValue);
      const items = res?.items || [];
      
      // Log daily prep fetch
      await loggingService.logInfo('central_kitchen_fetch_daily_prep', 'Fetched daily prep list', {
        date: prepDateValue,
        itemCount: items.length,
        hasOutletFilter: !!prepOutletFilter,
      });
      const uniqueItemIds = [...new Set(items.map((row) => row.item_id).filter(Boolean))];
      const itemDetailsMap = {};
      await Promise.all(
        uniqueItemIds.map(async (itemId) => {
          try {
            const data = await apiGetStockItem(itemId);
            const item = data?.item;
            if (item) {
              itemDetailsMap[itemId] = {
                item_name: item.item_name || '—',
                unit: item.unit_of_measure || '',
              };
            }
          } catch (err) {
            console.warn(`Failed to fetch stock item ${itemId}:`, err);
          }
        })
      );
      const normalized = items.map((row) => {
        const breakdown = row.outlets_breakdown || [];
        const outlets = breakdown.map((b) => {
          const outletId = b.outlet_id ?? (typeof b === 'object' ? Object.keys(b).find((k) => k !== 'requested_quantity' && k !== 'quantity' && k !== 'note' && k !== 'notes') : null);
          const qty = b.quantity ?? b.requested_quantity ?? (outletId && b[outletId]) ?? 0;
          const outlet = listForNormalize.find((s) => (s.site_id || s.id) === outletId);
          return {
            outlet_id: outletId,
            outlet_name: outlet?.site_name || outlet?.name || outletId || '—',
            quantity: Number(qty),
            notes: b.notes ?? b.note ?? '',
          };
        });
        const details = row.item_id ? itemDetailsMap[row.item_id] : null;
        return {
          item_id: row.item_id,
          item_name: details?.item_name ?? row.item_name ?? row.item_id ?? '—',
          unit: details?.unit || row.unit || '',
          total_quantity: Number(row.total_quantity ?? 0),
          outlets,
        };
      });
      setDailyPrepItems(normalized.length > 0 ? normalized : []);
    } catch (e) {
      console.error('Failed to fetch daily prep:', e);
      setDailyPrepError(e?.message || 'Failed to load daily prep');
      setDailyPrepItems([]);
      
      // Log error
      await loggingService.logError('central_kitchen_fetch_daily_prep_error', 'Failed to fetch daily prep', e, {
        date: prepDateValue,
        errorMessage: e?.message,
      });
    } finally {
      setDailyPrepLoading(false);
    }
  }, [sites]);

  const fetchSites = useCallback(async () => {
    try {
      const response = await getAllSites();
      const list = Array.isArray(response?.sites) ? response.sites : Array.isArray(response) ? response : [];
      const sorted = [...list].sort((a, b) => (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || ''));
      setSites(sorted.length > 0 ? sorted : []);
    } catch (e) {
      setSites([]);
    }
  }, []);

  const fetchStockItems = useCallback(async (siteId) => {
    setStockItemsLoading(true);
    try {
      const res = await apiGetStockItems(siteId ? { site_id: siteId } : {});
      setStockItems(res?.items ?? []);
    } catch (e) {
      console.error('Failed to fetch stock items:', e);
      setStockItems([]);
    } finally {
      setStockItemsLoading(false);
    }
  }, []);

  const fetchAllStockItemsForNames = useCallback(async () => {
    try {
      const res = await apiGetStockItems({});
      setAllStockItemsForNames(res?.items ?? []);
    } catch (e) {
      console.error('Failed to fetch stock items for names:', e);
      setAllStockItemsForNames([]);
    }
  }, []);

  useEffect(() => {
    fetchSites();
    fetchAllStockItemsForNames();
  }, [fetchSites, fetchAllStockItemsForNames]);

  useEffect(() => {
    if (requisitionsError) return;
    fetchRequisitions();
  }, [fetchRequisitions, requisitionsError]);

  useEffect(() => {
    if (activeTab !== TABS.DAILY_PREP || dailyPrepError) return;
    fetchDailyPrep(prepDate);
  }, [activeTab, prepDate, fetchDailyPrep, dailyPrepError]);

  useEffect(() => {
    if (!vehicleModal) {
      setVehicles([]);
      setVehiclesError(null);
      return;
    }
    const siteId = vehicleModal.outlet_id || null;
    if (!siteId) {
      setVehicles([]);
      setVehiclesError('No outlet/site for this transfer.');
      return;
    }
    setVehiclesLoading(true);
    setVehiclesError(null);
    apiGetVehiclesBySiteId(siteId)
      .then((list) => {
        console.log('[CentralKitchen] Vehicles API response:', list);
        setVehicles(Array.isArray(list) ? list : []);
      })
      .catch((e) => setVehiclesError(e?.message || 'Failed to load vehicles'))
      .finally(() => setVehiclesLoading(false));
  }, [vehicleModal]);

  const filteredRequisitions = requisitions.filter((r) => {
    if (reqSiteFilter && r.outlet_id !== reqSiteFilter) return false;
    if (reqStatusFilter && r.status !== reqStatusFilter) return false;
    if (reqDateFrom && r.requested_date < reqDateFrom) return false;
    if (reqDateTo && r.requested_date > reqDateTo) return false;
    return true;
  });

  const filteredDailyPrep = prepOutletFilter
    ? dailyPrepItems.map((row) => ({
        ...row,
        outlets: row.outlets.filter((o) => (o.outlet_id || o.outlet_name) === prepOutletFilter),
        total_quantity: row.outlets.filter((o) => (o.outlet_id || o.outlet_name) === prepOutletFilter).reduce((s, o) => s + o.quantity, 0),
      })).filter((row) => row.outlets.length > 0)
    : dailyPrepItems;

  const summaryDailyPrep = {
    total_requisitions: requisitions.filter((r) => r.status !== REQ_STATUS.DRAFT && r.status !== REQ_STATUS.CANCELLED && r.needed_by_date === prepDate).length,
    total_line_items: filteredDailyPrep.length,
    total_quantity: filteredDailyPrep.reduce((s, r) => s + r.total_quantity, 0),
    outlets_count: [...new Set(requisitions.filter((r) => r.needed_by_date === prepDate).map((r) => r.outlet_id))].length,
  };

  const openCreateRequisition = async () => {
    setRequisitionForm({
      outlet_id: '',
      needed_by_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
      line_items: [{ item_id: '', quantity: '', unit: '', notes: '' }],
    });
    setRequisitionModal({ mode: 'create' });
    // Don't fetch stock items until an outlet is selected
    
    // Log modal open
    await loggingService.logInfo('central_kitchen_open_create_modal', 'Opened create requisition modal');
  };

  const openEditRequisition = async (req) => {
    setRequisitionForm({
      outlet_id: req.outlet_id,
      needed_by_date: req.needed_by_date,
      line_items: req.line_items?.length ? req.line_items.map((li) => ({ ...li, quantity: String(li.quantity), item_id: li.item_id || '', unit: li.unit || '', notes: li.notes || '' })) : [{ item_id: '', quantity: '', unit: '', notes: '' }],
    });
    setRequisitionModal({ mode: 'edit', requisition: req });
    fetchStockItems(req.outlet_id || undefined);
    
    // Log modal open
    await loggingService.logInfo('central_kitchen_open_edit_modal', 'Opened edit requisition modal', {
      requisitionId: req.requisition_id,
      outletId: req.outlet_id,
      status: req.status,
    });
  };

  const addRequisitionLine = () => {
    setRequisitionForm((prev) => ({ ...prev, line_items: [...prev.line_items, { item_id: '', quantity: '', unit: '', notes: '' }] }));
  };

  const removeRequisitionLine = (index) => {
    setRequisitionForm((prev) => ({ ...prev, line_items: prev.line_items.filter((_, i) => i !== index) }));
  };

  const updateRequisitionLine = (index, field, value) => {
    setRequisitionForm((prev) => {
      const next = [...prev.line_items];
      next[index] = { ...next[index], [field]: value };
      if (field === 'item_id') {
        const item = stockItems.find((i) => (i.stock_item_id) === value);
        if (item) next[index].unit = item.unit || item.unit_of_measure || '';
      }
      return { ...prev, line_items: next };
    });
  };

  const saveRequisition = async (asDraft) => {
    if (!requisitionForm.outlet_id) return; // Require outlet selection
    if (!requisitionForm.needed_by_date) return;
    const neededBy = requisitionForm.needed_by_date;
    if (requisitionModal?.mode === 'create' && neededBy < today()) return;
    const line_items = requisitionForm.line_items
      .filter((li) => li.item_id && li.quantity)
      .map((li) => {
        const item = stockItems.find((i) => (i.stock_item_id) === li.item_id);
        const itemName = item?.item_name || '';
        const unit = li.unit || item?.unit || item?.unit_of_measure || '';
        return { item_id: li.item_id, item_name: itemName, quantity: Number(li.quantity), unit, notes: li.notes || '' };
      });
    if (!line_items.length) return;

    if (requisitionModal?.mode === 'create') {
      setSaveInProgress(true);
      try {
        await apiCreateRequisition({
          outlet_id: requisitionForm.outlet_id,
          needed_by_date: requisitionForm.needed_by_date,
          transfer_status: asDraft ? REQ_STATUS.DRAFT : REQ_STATUS.SUBMITTED,
          line_items: line_items.map((li) => ({
            item_id: li.item_id,
            requested_quantity: li.quantity,
            unit: li.unit,
            notes: li.notes,
          })),
        });
        
        // Log requisition creation
        await loggingService.logInfo('central_kitchen_create_requisition', 'Created requisition', {
          outletId: requisitionForm.outlet_id,
          neededByDate: requisitionForm.needed_by_date,
          status: asDraft ? REQ_STATUS.DRAFT : REQ_STATUS.SUBMITTED,
          lineItemCount: line_items.length,
          asDraft,
        });
        
        await fetchRequisitions();
      } catch (e) {
        console.error('Failed to create requisition:', e);
        
        // Log error
        await loggingService.logError('central_kitchen_create_requisition_error', 'Failed to create requisition', e, {
          outletId: requisitionForm.outlet_id,
          lineItemCount: line_items.length,
        });
        const newReq = {
          requisition_id: `req-${Date.now()}`,
          outlet_id: requisitionForm.outlet_id,
          outlet_name: siteList.find((s) => (s.site_id || s.id) === requisitionForm.outlet_id)?.site_name || requisitionForm.outlet_id,
          requested_items_summary: line_items.map((li) => `${li.item_name} ${li.quantity} ${li.unit}`).join(', '),
          item_count: line_items.length,
          requested_date: today(),
          needed_by_date: requisitionForm.needed_by_date,
          status: asDraft ? REQ_STATUS.DRAFT : REQ_STATUS.SUBMITTED,
          delivery_vehicle_id: null,
          delivery_vehicle_name: null,
          line_items,
        };
        setRequisitions((prev) => [...prev, newReq]);
      } finally {
        setSaveInProgress(false);
      }
    } else if (requisitionModal?.mode === 'edit' && requisitionModal.requisition) {
      const updated = {
        ...requisitionModal.requisition,
        outlet_id: requisitionForm.outlet_id,
        outlet_name: siteList.find((s) => (s.site_id || s.id) === requisitionForm.outlet_id)?.site_name || requisitionForm.outlet_id,
        requested_items_summary: line_items.map((li) => `${li.item_name} ${li.quantity} ${li.unit}`).join(', '),
        item_count: line_items.length,
        needed_by_date: requisitionForm.needed_by_date,
        status: asDraft ? REQ_STATUS.DRAFT : requisitionModal.requisition.status === REQ_STATUS.DRAFT ? REQ_STATUS.SUBMITTED : requisitionModal.requisition.status,
        line_items,
      };
      
      // Log requisition edit
      await loggingService.logInfo('central_kitchen_edit_requisition', 'Edited requisition', {
        requisitionId: requisitionModal.requisition.requisition_id,
        outletId: requisitionForm.outlet_id,
        previousStatus: requisitionModal.requisition.status,
        newStatus: updated.status,
        lineItemCount: line_items.length,
        asDraft,
      });
      
      setRequisitions((prev) => prev.map((r) => (r.requisition_id === updated.requisition_id ? updated : r)));
    }
    setRequisitionModal(null);
  };

  const cancelRequisition = async (req) => {
    setCancelRequisitionConfirm(null);
    setSaveInProgress(true);
    try {
      await apiUpdateRequisition(req.requisition_id, { transfer_status: REQ_STATUS.CANCELLED });
      
      // Log requisition cancellation
      await loggingService.logInfo('central_kitchen_cancel_requisition', 'Cancelled requisition', {
        requisitionId: req.requisition_id,
        outletId: req.outlet_id,
        previousStatus: req.status,
      });
      
      await fetchRequisitions();
    } catch (e) {
      console.error('Failed to cancel requisition:', e);
      
      // Log error
      await loggingService.logError('central_kitchen_cancel_requisition_error', 'Failed to cancel requisition', e, {
        requisitionId: req.requisition_id,
      });
      
      setRequisitions((prev) => prev.map((r) => (r.requisition_id === req.requisition_id ? { ...r, status: REQ_STATUS.CANCELLED } : r)));
    } finally {
      setSaveInProgress(false);
    }
  };

  const updateTransferStatus = async (transfer, newStatus) => {
    setTransferStatusModal(null);
    setSaveInProgress(true);
    try {
      const res = await apiUpdateRequisition(transfer.requisition_id, { transfer_status: newStatus });
      console.log('[CentralKitchen] Update status API response:', res);
      
      // Log transfer status update
      await loggingService.logInfo('central_kitchen_update_transfer_status', 'Updated transfer status', {
        requisitionId: transfer.requisition_id,
        previousStatus: transfer.status,
        newStatus,
        outletId: transfer.outlet_id,
      });
      
      await fetchRequisitions();
    } catch (e) {
      console.error('Failed to update transfer status:', e);
      
      // Log error
      await loggingService.logError('central_kitchen_update_transfer_status_error', 'Failed to update transfer status', e, {
        requisitionId: transfer.requisition_id,
        newStatus,
      });
    } finally {
      setSaveInProgress(false);
    }
  };

  const setTransferVehicle = async (transfer, vehicleId) => {
    setSaveInProgress(true);
    try {
      const res = await apiUpdateRequisition(transfer.requisition_id, {
        delivery_vehicle_id: vehicleId,
        transfer_status: REQ_STATUS.DISPATCHED,
      });
      console.log('[CentralKitchen] Set vehicle (updateRequisition) API response:', res);
      
      // Log vehicle assignment
      await loggingService.logInfo('central_kitchen_set_transfer_vehicle', 'Set vehicle for transfer', {
        requisitionId: transfer.requisition_id,
        vehicleId,
        outletId: transfer.outlet_id,
        previousStatus: transfer.status,
      });
      
      await fetchRequisitions();
    } catch (e) {
      console.error('Failed to set vehicle:', e);
      
      // Log error
      await loggingService.logError('central_kitchen_set_transfer_vehicle_error', 'Failed to set vehicle for transfer', e, {
        requisitionId: transfer.requisition_id,
        vehicleId,
      });
    } finally {
      setSaveInProgress(false);
      setVehicleModal(null);
      setSelectedVehicleId('');
    }
  };

  const siteList = sites.length ? sites : [];
  const transfers = React.useMemo(
    () => requisitionsToTransfers(requisitions, siteList),
    [requisitions, siteList]
  );

  return (
    <div className="w-full h-full">
      <div className="mb-6">
        <h1 className={`${typography.h2} text-black mb-1`}>Central kitchen</h1>
        <p className={`${typography.p} text-gray-600 text-sm`}>
          Requisitions, daily prep list, and transfers
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {[
          { id: TABS.REQUISITIONS, label: 'Requisitions', icon: ClipboardList },
          { id: TABS.DAILY_PREP, label: 'Daily prep list', icon: Package },
          { id: TABS.TRANSFERS, label: 'Transfers', icon: Truck },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={async () => {
              const previousTab = activeTab;
              setActiveTab(id);
              // Log tab switch
              if (id !== previousTab) {
                await loggingService.logInfo('central_kitchen_tab_switch', 'Switched tab', {
                  tab: id,
                  previousTab,
                });
              }
            }}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors ${
              activeTab === id
                ? 'border-emerald-600 text-emerald-700 bg-emerald-50'
                : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Requisitions tab */}
      {activeTab === TABS.REQUISITIONS && (
        <>
          {requisitionsError && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 text-amber-800 text-sm flex items-center justify-between gap-3 flex-wrap">
              <span>{requisitionsError}</span>
              <button
                type="button"
                onClick={() => setRequisitionsError(null)}
                className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 whitespace-nowrap"
              >
                Retry
              </button>
            </div>
          )}
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Outlet</label>
              <select
                value={reqSiteFilter}
                onChange={(e) => setReqSiteFilter(e.target.value)}
                className="h-10 px-4 rounded-lg border border-gray-300 bg-white text-sm min-w-[180px] focus:border-green-500 focus:ring-2 focus:ring-green-500/40"
              >
                <option value="">All outlets</option>
                {siteList.map((s) => (
                  <option key={s.site_id || s.id} value={s.site_id || s.id}>{s.site_name || s.name}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Requested at</label>
              <input
                type="date"
                value={reqDateFrom}
                max={reqDateTo || undefined}
                onChange={(e) => {
                  const val = e.target.value;
                  setReqDateFrom(val);
                  if (val && reqDateTo && val > reqDateTo) setReqDateTo(val);
                }}
                placeholder="From"
                title="Start date must be before or equal to end date"
                className="h-10 px-4 rounded-lg border border-gray-300 bg-white text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500/40"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Needed by</label>
              <input
                type="date"
                value={reqDateTo}
                min={reqDateFrom || undefined}
                onChange={(e) => {
                  const val = e.target.value;
                  setReqDateTo(val);
                  if (val && reqDateFrom && val < reqDateFrom) setReqDateFrom(val);
                }}
                placeholder="To"
                title="End date must be on or after start date"
                className="h-10 px-4 rounded-lg border border-gray-300 bg-white text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500/40"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Status</label>
              <select
                value={reqStatusFilter}
                onChange={(e) => setReqStatusFilter(e.target.value)}
                className="h-10 px-4 rounded-lg border border-gray-300 bg-white text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500/40"
              >
                <option value="">All statuses</option>
                {Object.entries(REQ_STATUS_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={openCreateRequisition}
              className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 flex items-center gap-2 text-sm font-medium !text-white"
            >
              <Plus className="w-4 h-4 shrink-0 !text-white" />
              <span className="!text-white">Create requisition</span>
            </button>
          </div>
          <Card variant="solid" className="p-4">
            {requisitionsLoading && (
              <div className="py-4 text-center text-gray-500 text-sm">Loading requisitions…</div>
            )}
            {!requisitionsLoading && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-600">
                    <th className="pb-2 pr-4 font-medium">Outlet</th>
                    <th className="pb-2 pr-4 font-medium">Items</th>
                    <th className="pb-2 pr-4 font-medium">Requested at</th>
                    <th className="pb-2 pr-4 font-medium">Needed by</th>
                    <th className="pb-2 pr-4 font-medium">Status</th>
                    <th className="pb-2 pr-4 font-medium">Vehicle</th>
                    <th className="pb-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequisitions.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-6 text-center text-gray-500">No requisitions</td>
                    </tr>
                  ) : (
                    filteredRequisitions.map((r) => (
                      <tr key={r.requisition_id} className="border-b border-gray-100">
                        <td className="py-2 pr-4">{r.outlet_name}</td>
                        <td className="py-2 pr-4 text-gray-600">{r.requested_items_summary}</td>
                        <td className="py-2 pr-4 text-gray-600">{formatDate(r.requested_date)}</td>
                        <td className="py-2 pr-4 text-gray-600">{formatDate(r.needed_by_date)}</td>
                        <td className="py-2 pr-4">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                            r.status === REQ_STATUS.DELIVERED ? 'bg-emerald-100 text-emerald-800' :
                            r.status === REQ_STATUS.CANCELLED ? 'bg-gray-200 text-gray-600' :
                            r.status === REQ_STATUS.DRAFT ? 'bg-gray-100 text-gray-700' : 'bg-amber-100 text-amber-800'
                          }`}>
                            {REQ_STATUS_LABELS[r.status] || r.status}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-gray-600">{r.delivery_vehicle_name || '—'}</td>
                        <td className="py-2 flex gap-2">
                          {r.status === REQ_STATUS.DRAFT && (
                            <button type="button" onClick={() => openEditRequisition(r)} className="text-blue-600 hover:text-blue-700 text-xs font-medium">Edit</button>
                          )}
                          {r.status !== REQ_STATUS.CANCELLED && r.status !== REQ_STATUS.DELIVERED && (
                            <button type="button" onClick={() => setCancelRequisitionConfirm(r)} className="text-red-600 hover:text-red-700 text-xs font-medium">Cancel</button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            )}
          </Card>
        </>
      )}

      {/* Daily prep list tab */}
      {activeTab === TABS.DAILY_PREP && (
        <>
          {dailyPrepError && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 text-amber-800 text-sm flex items-center justify-between gap-3 flex-wrap">
              <span>{dailyPrepError}</span>
              <button
                type="button"
                onClick={() => setDailyPrepError(null)}
                className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 whitespace-nowrap"
              >
                Retry
              </button>
            </div>
          )}
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Date</label>
              <input
                type="date"
                value={prepDate}
                onChange={(e) => setPrepDate(e.target.value)}
                className="h-10 px-4 rounded-lg border border-gray-300 bg-white text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500/40"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Filter by outlet</label>
              <select
                value={prepOutletFilter}
                onChange={(e) => setPrepOutletFilter(e.target.value)}
                className="h-10 px-4 rounded-lg border border-gray-300 bg-white text-sm min-w-[180px] focus:border-green-500 focus:ring-2 focus:ring-green-500/40"
              >
                <option value="">All outlets</option>
                {siteList.map((s) => (
                  <option key={s.site_id || s.id} value={s.site_id || s.id}>{s.site_name || s.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card variant="solid" className="p-4">
              <p className={`${typography.sh3} text-gray-500 mb-1`}>Requisitions for day</p>
              <p className={`${typography.h4} text-black`}>{summaryDailyPrep.total_requisitions}</p>
            </Card>
            <Card variant="solid" className="p-4">
              <p className={`${typography.sh3} text-gray-500 mb-1`}>Line items (unique)</p>
              <p className={`${typography.h4} text-black`}>{summaryDailyPrep.total_line_items}</p>
            </Card>
            <Card variant="solid" className="p-4">
              <p className={`${typography.sh3} text-gray-500 mb-1`}>Total quantity</p>
              <p className={`${typography.h4} text-black`}>{summaryDailyPrep.total_quantity}</p>
            </Card>
            <Card variant="solid" className="p-4">
              <p className={`${typography.sh3} text-gray-500 mb-1`}>Outlets</p>
              <p className={`${typography.h4} text-black`}>{summaryDailyPrep.outlets_count}</p>
            </Card>
          </div>
          <Card variant="solid" className="p-4">
            <h3 className={`${typography.sh2} text-black mb-4`}>Daily prep list</h3>
            {dailyPrepLoading && (
              <div className="py-4 text-center text-gray-500 text-sm">Loading daily prep…</div>
            )}
            {!dailyPrepLoading && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-600">
                    <th className="pb-2 pr-4 font-medium">Item</th>
                    <th className="pb-2 pr-4 font-medium">Total qty</th>
                    <th className="pb-2 pr-4 font-medium">Unit</th>
                    <th className="pb-2 pr-4 font-medium">Outlets</th>
                    <th className="pb-2 font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDailyPrep.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-6 text-center text-gray-500">No items for this day</td>
                    </tr>
                  ) : (
                    filteredDailyPrep.map((row) => (
                      <tr key={row.item_id} className="border-b border-gray-100">
                        <td className="py-2 pr-4 font-medium align-top">{row.item_name}</td>
                        <td className="py-2 pr-4 align-top">{row.total_quantity}</td>
                        <td className="py-2 pr-4 text-gray-600 align-top">{row.unit}</td>
                        <td className="py-2 pr-4 text-gray-600 align-top">
                          <div className="flex flex-col gap-2">
                            {row.outlets.map((o, outIdx) => (
                              <div key={`${row.item_id}-outlet-${outIdx}`} className="leading-tight mb-2">
                                {o.outlet_name}: {o.quantity} {row.unit}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="py-2 text-gray-500 align-top">
                          <div className="flex flex-col gap-2">
                            {row.outlets.map((o, outIdx) => (
                              <div key={`${row.item_id}-notes-${outIdx}`} className="leading-tight mb-2">
                                {o.notes || '—'}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            )}
          </Card>
        </>
      )}

      {/* Transfers tab */}
      {activeTab === TABS.TRANSFERS && (
        <>
          <Card variant="solid" className="p-4">
            <h3 className={`${typography.sh2} text-black mb-4`}>Transfers</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-600">
                    {/* <th className="pb-2 pr-4 font-medium">Requisition / Batch</th> */}
                    <th className="pb-2 pr-4 font-medium">Outlet</th>
                    <th className="pb-2 pr-4 font-medium">Items</th>
                    <th className="pb-2 pr-4 font-medium">Updated at</th>
                    <th className="pb-2 pr-4 font-medium">Vehicle</th>
                    <th className="pb-2 pr-4 font-medium">Status</th>
                    <th className="pb-2 pr-4 font-medium">Expected by</th>
                    <th className="pb-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transfers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-6 text-center text-gray-500">No transfers</td>
                    </tr>
                  ) : (
                    transfers.map((t) => (
                      <tr key={t.transfer_id} className="border-b border-gray-100">
                        {/* <td className="py-2 pr-4 font-medium">{t.transfer_id}</td> */}
                        <td className="py-2 pr-4">{t.outlet_name}</td>
                        <td className="py-2 pr-4 text-gray-600">{t.items_summary}</td>
                        <td className="py-2 pr-4 text-gray-600">{t.updated_at ? new Date(t.updated_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                        <td className="py-2 pr-4 text-gray-600">{t.delivery_vehicle_name || t.delivery_vehicle_id || '—'}</td>
                        <td className="py-2 pr-4">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                            t.status === REQ_STATUS.DELIVERED ? 'bg-emerald-100 text-emerald-800' :
                            t.status === REQ_STATUS.IN_TRANSIT ? 'bg-amber-100 text-amber-800' :
                            t.status === REQ_STATUS.DISPATCHED ? 'bg-blue-100 text-blue-800' :
                            t.status === REQ_STATUS.SUBMITTED ? 'bg-slate-100 text-slate-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {REQ_STATUS_LABELS[t.status] || t.status}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-gray-600">{formatDate(t.expected_by)}</td>
                        <td className="py-2 flex gap-2">
                          <button type="button" onClick={() => { setTransferStatusModal(t); setTransferStatusValue(t.status); }} className="text-blue-600 hover:text-blue-700 text-xs font-medium">Update status</button>
                          <button type="button" onClick={() => { setVehicleModal(t); setSelectedVehicleId(t.delivery_vehicle_id || ''); }} className="text-blue-600 hover:text-blue-700 text-xs font-medium">Set vehicle</button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* Cancel requisition warning dialog */}
      {cancelRequisitionConfirm && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center min-h-screen w-full overflow-y-auto p-4 bg-black/50" onClick={() => setCancelRequisitionConfirm(null)} role="dialog" aria-modal="true" aria-labelledby="cancel-warning-title">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-4 my-auto" onClick={(e) => e.stopPropagation()}>
            <h2 id="cancel-warning-title" className={`${typography.h4} text-amber-700 mb-2`}>Cancel requisition?</h2>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to cancel requisition <strong>{cancelRequisitionConfirm.requisition_id}</strong> for {cancelRequisitionConfirm.outlet_name}? This action will mark the requisition as cancelled.
            </p>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setCancelRequisitionConfirm(null)} disabled={saveInProgress} className="h-10 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium disabled:opacity-50">Keep</button>
              <button type="button" onClick={() => cancelRequisition(cancelRequisitionConfirm)} disabled={saveInProgress} className="h-10 px-4 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm font-medium disabled:opacity-50">Yes, cancel</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Create/Edit Requisition modal */}
      {requisitionModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center min-h-screen w-full overflow-y-auto p-4 bg-black/50" onClick={() => setRequisitionModal(null)} role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full my-8 p-4 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`${typography.h4} text-black`}>{requisitionModal.mode === 'create' ? 'Create requisition' : 'Edit requisition'}</h2>
              <button type="button" onClick={() => setRequisitionModal(null)} className="text-gray-500 hover:text-black p-1 rounded hover:bg-gray-100" aria-label="Close"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Outlet</label>
                <select
                  value={requisitionForm.outlet_id}
                  onChange={(e) => {
                    const outletId = e.target.value;
                    setRequisitionForm((prev) => ({ ...prev, outlet_id: outletId }));
                    fetchStockItems(outletId || undefined);
                  }}
                  required
                  className="w-full h-10 px-4 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-500/40 text-sm"
                >
                  <option value="">Select outlet</option>
                  {siteList.map((s) => (
                    <option key={s.site_id || s.id} value={s.site_id || s.id}>{s.site_name || s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Needed by date *</label>
                <input
                  type="date"
                  value={requisitionForm.needed_by_date}
                  min={requisitionModal?.mode === 'create' ? today() : undefined}
                  required
                  onChange={(e) => setRequisitionForm((prev) => ({ ...prev, needed_by_date: e.target.value }))}
                  title="Required. Must be today or a future date."
                  className="w-full h-10 px-4 rounded-lg border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-500/40 text-sm"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Line items</label>
                  {stockItemsLoading && <span className="text-xs text-gray-500">Loading items…</span>}
                  <button type="button" onClick={addRequisitionLine} className="text-emerald-600 hover:text-emerald-700 text-sm font-medium">Add line</button>
                </div>
                <div className="space-y-2">
                  {requisitionForm.line_items.map((li, index) => (
                    <div key={index} className="flex flex-wrap items-center gap-2">
                      <select
                        value={li.item_id}
                        onChange={(e) => updateRequisitionLine(index, 'item_id', e.target.value)}
                        className="h-10 px-3 rounded-lg border border-gray-300 bg-white text-sm flex-1 min-w-[120px] focus:border-green-500 focus:ring-2 focus:ring-green-500/40"
                        disabled={stockItemsLoading}
                      >
                        <option value="">Select item</option>
                        {stockItems.map((i) => {
                          const id = i.stock_item_id;
                          const name = i.item_name || i.name || id || '—';
                          const u = i.unit || i.unit_of_measure || '';
                          return (
                            <option key={id} value={id}>{name}{u ? ` (${u})` : ''}</option>
                          );
                        })}
                      </select>
                      <input
                        type="number"
                        value={li.quantity}
                        onChange={(e) => updateRequisitionLine(index, 'quantity', e.target.value)}
                        placeholder="Qty"
                        min="1"
                        className="h-10 w-20 px-3 rounded-lg border border-gray-300 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500/40"
                      />
                      <input
                        type="text"
                        value={li.notes}
                        onChange={(e) => updateRequisitionLine(index, 'notes', e.target.value)}
                        placeholder="Notes"
                        className="h-10 flex-1 min-w-[80px] px-3 rounded-lg border border-gray-300 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-500/40"
                      />
                      <button type="button" onClick={() => removeRequisitionLine(index)} className="text-red-600 hover:text-red-700 p-1" aria-label="Remove">×</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button type="button" onClick={() => setRequisitionModal(null)} disabled={saveInProgress} className="h-10 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium disabled:opacity-50">Cancel</button>
              {requisitionModal.mode === 'create' && (
                <button type="button" onClick={() => saveRequisition(true)} disabled={saveInProgress} className="h-10 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium disabled:opacity-50">Save as draft</button>
              )}
              <button type="button" onClick={() => saveRequisition(false)} disabled={saveInProgress} className="h-10 px-4 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-50">
                {requisitionModal.mode === 'create' ? 'Submit' : requisitionModal.requisition?.status === REQ_STATUS.DRAFT ? 'Submit' : 'Save'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Update transfer status modal */}
      {transferStatusModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center min-h-screen w-full overflow-y-auto p-4 bg-black/50" onClick={() => setTransferStatusModal(null)} role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-4 my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`${typography.h4} text-black`}>Update status</h2>
              <button type="button" onClick={() => setTransferStatusModal(null)} className="text-gray-500 hover:text-black p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-gray-600 mb-2">Requisition: {transferStatusModal.outlet_name}</p>
            <select
              value={transferStatusValue}
              onChange={(e) => setTransferStatusValue(e.target.value)}
              className="w-full h-10 px-4 rounded-lg border border-gray-300 text-sm mb-4 focus:border-green-500 focus:ring-2 focus:ring-green-500/40"
            >
              {[REQ_STATUS.DISPATCHED, REQ_STATUS.IN_TRANSIT, REQ_STATUS.DELIVERED].map((s) => (
                <option key={s} value={s}>{REQ_STATUS_LABELS[s]}</option>
              ))}
            </select>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setTransferStatusModal(null)} disabled={saveInProgress} className="h-10 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium disabled:opacity-50">Cancel</button>
              <button type="button" onClick={() => updateTransferStatus(transferStatusModal, transferStatusValue)} disabled={saveInProgress} className="h-10 px-4 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-50">Save</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Set delivery vehicle modal */}
      {vehicleModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center min-h-screen w-full overflow-y-auto p-4 bg-black/50" onClick={() => setVehicleModal(null)} role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-4 my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`${typography.h4} text-black`}>Set delivery vehicle</h2>
              <button type="button" onClick={() => setVehicleModal(null)} className="text-gray-500 hover:text-black p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-gray-600 mb-2">Requisition: {vehicleModal.outlet_name}</p>
            {vehiclesError && (
              <p className="text-sm text-amber-600 mb-2">{vehiclesError}</p>
            )}
            <select
              value={selectedVehicleId}
              onChange={(e) => setSelectedVehicleId(e.target.value)}
              disabled={vehiclesLoading}
              className="w-full h-10 px-4 rounded-lg border border-gray-300 text-sm mb-4 focus:border-green-500 focus:ring-2 focus:ring-green-500/40 disabled:opacity-60"
            >
              <option value="">{vehiclesLoading ? 'Loading vehicles…' : 'Select vehicle'}</option>
              {vehicles.map((v) => (
                <option key={v.vehicle_id} value={v.vehicle_id}>
                  {v.model_name && v.registration_no ? `${v.model_name} (${v.registration_no})` : (v.model_name || v.registration_no || v.vehicle_id)}
                </option>
              ))}
            </select>
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setVehicleModal(null)} disabled={saveInProgress} className="h-10 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium disabled:opacity-50">Cancel</button>
              <button type="button" onClick={() => setTransferVehicle(vehicleModal, selectedVehicleId)} disabled={saveInProgress || !selectedVehicleId} className="h-10 px-4 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-50">Save</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CentralKitchen;
